import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { Button, Field, Select } from "../lib/ui.js";
import { errorStyle, linkButtonStyle, mutedStyle, tdStyle, thStyle } from "./settings/common.js";
import { formatClock } from "../lib/datetime.js";
import { TimeInput12 } from "../lib/TimeInput12.js";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** Monday first, the way a week reads on a shop door. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface ResourceRow {
  id: string;
  name: string;
}

interface AvailabilityRow {
  id: string;
  resource_id: string;
  weekday: number;
  start_time: string;
  end_time: string;
  resources: { name: string } | null;
}

/** What is wrong with adding these hours (the same staff member, same day), or null. Hours that only touch (9-12 then 12-5) are fine. */
export function hoursProblem(existing: { weekday: number; start_time: string; end_time: string }[], weekday: number, start: string, end: string): string | null {
  const hm = (t: string) => t.slice(0, 5);
  if (hm(end) <= hm(start)) return "The end time must be after the start time.";
  for (const a of existing) {
    if (a.weekday !== weekday) continue;
    if (hm(a.start_time) === hm(start) && hm(a.end_time) === hm(end)) return `Already added: ${WEEKDAYS[weekday]} ${formatClock(start)} - ${formatClock(end)}.`;
    if (hm(start) < hm(a.end_time) && hm(end) > hm(a.start_time)) {
      return `These hours overlap ${WEEKDAYS[weekday]} ${formatClock(a.start_time)} - ${formatClock(a.end_time)}. Remove or change that one first.`;
    }
  }
  return null;
}

/** When each staff member can take bookings (what decides whether a time can be booked, on the phone and on the web).
 *  Lives on the Services & staff page, next to the staff list. `reloadKey` re-reads the staff after the list above changes. */
export function StaffHours({ reloadKey = 0 }: { reloadKey?: number }) {
  const { activeBusinessId } = useAuth();
  const readOnly = useReadOnly();
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [availability, setAvailability] = useState<AvailabilityRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [availResourceId, setAvailResourceId] = useState("");
  const [availWeekday, setAvailWeekday] = useState("1");
  const [availStart, setAvailStart] = useState("09:00");
  const [availEnd, setAvailEnd] = useState("17:00");

  async function load() {
    if (!activeBusinessId) return;
    const [{ data: res }, { data: avail }] = await Promise.all([
      supabase.from("resources").select("id, name").eq("business_id", activeBusinessId).order("name"),
      supabase
        .from("availability")
        .select("id, resource_id, weekday, start_time, end_time, resources(name)")
        .eq("business_id", activeBusinessId)
        .order("weekday"),
    ]);
    const resourceList = (res ?? []) as ResourceRow[];
    setResources(resourceList);
    setAvailability((avail ?? []) as unknown as AvailabilityRow[]);
    setAvailResourceId((cur) => (cur && resourceList.some((r) => r.id === cur) ? cur : resourceList[0]?.id || ""));
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId, reloadKey]);

  async function handleAddAvailability(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !availResourceId) return;
    setError(null);
    const who = resources.find((r) => r.id === availResourceId)?.name ?? "This staff member";
    const problem = hoursProblem(availability.filter((a) => a.resource_id === availResourceId), Number(availWeekday), availStart, availEnd);
    if (problem) {
      setError(`${who}: ${problem}`);
      return;
    }
    const { error: err } = await supabase.from("availability").insert({
      business_id: activeBusinessId,
      resource_id: availResourceId,
      weekday: Number(availWeekday),
      start_time: availStart,
      end_time: availEnd,
    });
    if (err) {
      // 23P01: the database's own no-overlap rule (another screen added overlapping hours a moment ago).
      setError(err.code === "23P01" ? `${who}: these hours overlap hours that were just added. Reload and check the list.` : err.message);
      await load();
      return;
    }
    await load();
  }

  async function handleDeleteAvailability(id: string) {
    await supabase.from("availability").delete().eq("id", id);
    await load();
  }

  return (
    <section>
      <h2 className="rc-h2" style={{ marginBottom: 4 }}>Staff working hours</h2>
      <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 12px" }}>
        When each staff member can take bookings. A time can only be booked inside these hours, on the phone and on the web.
      </p>
      {resources.length === 0 ? (
        <p style={mutedStyle}>Add staff above first.</p>
      ) : (
        <>
          {!readOnly && (
          <form onSubmit={handleAddAvailability} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field label="Staff">
              <Select value={availResourceId} onChange={(e) => setAvailResourceId(e.target.value)} style={{ width: 170 }}>
                {resources.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Day">
              <Select value={availWeekday} onChange={(e) => setAvailWeekday(e.target.value)} style={{ width: 140 }}>
                {WEEK_ORDER.map((i) => (
                  <option key={i} value={i}>
                    {WEEKDAYS[i]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Start">
              <TimeInput12 label="Start" value={availStart} onChange={setAvailStart} />
            </Field>
            <Field label="End">
              <TimeInput12 label="End" value={availEnd} onChange={setAvailEnd} />
            </Field>
            <Button type="submit" variant="primary">
              Add
            </Button>
          </form>
          )}
          <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Staff</th>
                <th style={thStyle}>Day</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {availability.map((a) => (
                <tr key={a.id}>
                  <td style={tdStyle}>{a.resources?.name ?? "-"}</td>
                  <td style={tdStyle}>{WEEKDAYS[a.weekday]}</td>
                  <td style={tdStyle}>
                    {formatClock(a.start_time)} - {formatClock(a.end_time)}
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && (
                      <button onClick={() => void handleDeleteAvailability(a.id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {availability.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, ...mutedStyle }}>
                    No staff hours set yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table></div>
        </>
      )}
      {error && <p style={errorStyle}>{error}</p>}
    </section>
  );
}
